import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { PIPEFITTER_TAB_TRIGGER_CLASS } from '../constants/uiConstants';
import {
  PIPEFITTER_FETCH_PIPE_STATISTICS,
  PIPEFITTER_FETCH_PROJECT_SUMMARY_DATA,
  PIPEFITTER_FETCH_PIPE_DATA_GRAPH,
  PIPEFITTER_FETCH_PROJECT_STATUS_PIPE_DATA,
  PIPEFITTER_FETCH_PIPE_LENGTH,
  PIPEFITTER_FETCH_PROJECT_REPORT,
  PIPEFITTER_FETCH_PROJECT_SUMMARY_REPORT,
} from '../../../redux/Pipefitter/types';
import type { DashboardProject } from '../../project/types/project';
import { ProjectStatus } from './ProjectStatus';
import { PipeData } from './PipeData';
import { PipeCompatibility } from './PipeCompatibility';
import { PipeLinearGraph } from './PipeLinearGraph';
import { PipePolarView } from './PipePolarView';
import { ProjectSummary } from './ProjectSummary';

type StructuredFilterType = 'all' | 'status' | 'pipeData' | 'compatibility' | 'summary';

interface ReportsProps {
  project: DashboardProject;
  canReport?: boolean;
  viewMode?: 'default' | 'structured';
  /** Currently selected pipe (optional). Used to scope report data to a specific pipe when provided. */
  selectedPipe?: { pipeId: string } | null;
  /** When viewMode is 'structured', optionally open with this report section focused (e.g. for Pipe Data Reports / Pipe Compatibility Report modals). */
  initialStructuredFilter?: StructuredFilterType;
}

// Optional: set in .env for testing when project has no projectNumber (e.g. REACT_APP_PIPEFITTER_DEMO_PROJECT_NUMBER=TEST001)
const DEMO_PROJECT_NUMBER = process.env.REACT_APP_PIPEFITTER_DEMO_PROJECT_NUMBER ?? '';

export const Reports: React.FC<ReportsProps> = ({
  project,
  canReport = true,
  viewMode = 'default',
  selectedPipe,
  initialStructuredFilter = 'all',
}: ReportsProps) => {
  const dispatch = useDispatch();
  const projectNumberFromProject = project?.project?.projectNumber;
  const projectNumber = projectNumberFromProject ?? DEMO_PROJECT_NUMBER ?? '';
  const [activeReportTab, setActiveReportTab] = useState('projectStatus');
  const [showGraphicalView, setShowGraphicalView] = useState(false);
  const [structuredFilter, setStructuredFilter] = useState<StructuredFilterType>(initialStructuredFilter);

  const {
    isReportsLoading,
    pipeStatistics,
    pipeStatusData,
    projectSummaryData,
    pipeDataGraph,
    projectStatusPipeData,
  } = useSelector((state: any) => state.pipefitterReports || {});

  const pipeCounts = useSelector((state: any) => state.pipefitterProject || {});

  const fetchData = (tabKey: string) => {
    if (!projectNumber && !DEMO_PROJECT_NUMBER) return;
    const effectiveProjectNumber = projectNumber || DEMO_PROJECT_NUMBER;
    const pipeId = selectedPipe?.pipeId;
    switch (tabKey) {
      case 'projectStatus':
        dispatch({
          type: PIPEFITTER_FETCH_PROJECT_STATUS_PIPE_DATA,
          payload: { pipeId: pipeId, projectNumber: project?.project?.projectNumber},
        });
        break;
      case 'pipeData':
        dispatch({
          type: PIPEFITTER_FETCH_PIPE_STATISTICS,
          payload: { pipeId: pipeId, projectNumber: project?.project?.projectNumber },
        });
        dispatch({
          type: PIPEFITTER_FETCH_PIPE_DATA_GRAPH,
          payload: { pipeId: pipeId, projectNumber: effectiveProjectNumber },
        });
        // dispatch({
        //   type: PIPEFITTER_FETCH_PIPE_LENGTH,
        //   payload: { pipeId: pipeId, projectNumber: effectiveProjectNumber, spreadId: '0', subSpreadId: '0' },
        // });
        break;
      // case 'projectSummary':
      //   dispatch({
      //     type: PIPEFITTER_FETCH_PROJECT_SUMMARY_DATA,
      //     payload: { pipeId: pipeId, projectNumber: effectiveProjectNumber },
      //   });
      //   break;
      case 'pipeCompatibility':
        dispatch({
          type: PIPEFITTER_FETCH_PROJECT_SUMMARY_DATA,
          payload: { pipeId: pipeId, projectNumber: effectiveProjectNumber },
        });
      //   break;
      // case 'pipeLinearGraph':
      // case 'pipePolarView':
        // dispatch({
        //   type: PIPEFITTER_FETCH_PIPE_DATA_GRAPH,
        //   payload: { pipeId, projectNumber: effectiveProjectNumber },
        // });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!projectNumber) return;

    if (viewMode === 'default') {
      fetchData(activeReportTab);
      setShowGraphicalView(false);
    } else {
      fetchData('projectStatus');
      fetchData('pipeData');
      fetchData('pipeCompatibility');
      fetchData('projectSummary');
    }
  }, [projectNumber, activeReportTab, viewMode]);

  if (viewMode === 'structured') {
    const showStatus = structuredFilter === 'all' || structuredFilter === 'status';
    const showPipeData = structuredFilter === 'all' || structuredFilter === 'pipeData';
    const showCompatibility = structuredFilter === 'all' || structuredFilter === 'compatibility';
    const showSummary = structuredFilter === 'all' || structuredFilter === 'summary';

    const totalNumberOfPipes = pipeCounts.totalPipeCount || 0;
    const availablePipes = pipeCounts.pipeCount || 0;
    const usedPipes = pipeCounts.usedPipeCount || 0;

    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-lg font-semibold">Project Reports Overview</h3>
                <p className="text-sm text-gray-600">
                  {project?.project?.projectName ?? 'Project'} · {projectNumber || DEMO_PROJECT_NUMBER}
                </p>
              </div>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex flex-col">
                  <span className="text-gray-500">Total Pipes</span>
                  <span className="font-semibold">{totalNumberOfPipes}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-gray-500">Available Pipes</span>
                  <span className="font-semibold">{availablePipes}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-gray-500">Used Pipes</span>
                  <span className="font-semibold">{usedPipes}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Report Filters</span>
                {isReportsLoading && (
                  <span className="text-xs text-gray-500">Loading latest report data…</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={structuredFilter === 'all' ? 'default' : 'outline'}
                  onClick={() => setStructuredFilter('all')}
                >
                  All
                </Button>
                <Button
                  size="sm"
                  variant={structuredFilter === 'status' ? 'default' : 'outline'}
                  onClick={() => setStructuredFilter('status')}
                >
                  Project Status
                </Button>
                <Button
                  size="sm"
                  variant={structuredFilter === 'pipeData' ? 'default' : 'outline'}
                  onClick={() => setStructuredFilter('pipeData')}
                >
                  Pipe Data
                </Button>
                <Button
                  size="sm"
                  variant={structuredFilter === 'compatibility' ? 'default' : 'outline'}
                  onClick={() => setStructuredFilter('compatibility')}
                >
                  Pipe Compatibility
                </Button>
                <Button
                  size="sm"
                  variant={structuredFilter === 'summary' ? 'default' : 'outline'}
                  onClick={() => setStructuredFilter('summary')}
                >
                  Project Summary
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
          {showStatus && (
            <ProjectStatus
              projectStatusPipeData={projectStatusPipeData}
              isLoading={isReportsLoading}
              projectNumber={projectNumber || DEMO_PROJECT_NUMBER}
            />
          )}
          {showPipeData && (
            <PipeData
              pipeStatistics={pipeStatistics}
              pipeDataGraph={pipeDataGraph}
              isLoading={isReportsLoading}
              handleSwitchChange={setShowGraphicalView}
              showGraphicalView={showGraphicalView}
              projectNumber={projectNumber || DEMO_PROJECT_NUMBER}
              canReport={canReport}
              selectedPipe={selectedPipe ?? null}
            />
          )}
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {showCompatibility && (
            <PipeCompatibility
              projectSummaryData={projectSummaryData}
              projectNumber={projectNumber || DEMO_PROJECT_NUMBER}
              isLoading={isReportsLoading}
              canReport={canReport}
            />
          )}
          {showSummary && (
            <ProjectSummary
              projectSummaryData={projectSummaryData}
              isLoading={isReportsLoading}
              projectNumber={projectNumber || DEMO_PROJECT_NUMBER}
              canReport={canReport}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
     <ProjectStatus
              projectStatusPipeData={projectStatusPipeData}
              isLoading={isReportsLoading}
              projectNumber={projectNumber || DEMO_PROJECT_NUMBER}
            />
    {false &&  <Tabs
        value={activeReportTab}
        onValueChange={(value) => {
          setActiveReportTab(value);
          fetchData(value);
        }}
        className="w-full"
      >
        <div className="sticky top-0 bg-background z-10 border-b border-border rounded-t-lg">
          <TabsList className="h-auto w-full justify-start gap-2 md:gap-4 lg:gap-8 border-b bg-transparent px-1 md:px-4 py-2 md:py-4 flex-wrap">
            <TabsTrigger value="projectStatus" className={PIPEFITTER_TAB_TRIGGER_CLASS}>
              <div className="flex items-center gap-1 md:gap-2">
                <span className="hidden sm:inline">Pipe Status</span>
                <span className="sm:hidden">Status</span>
              </div>
            </TabsTrigger>
            {/* <TabsTrigger value="pipeData" className={PIPEFITTER_TAB_TRIGGER_CLASS}>
              <div className="flex items-center gap-1 md:gap-2">
                <span className="hidden sm:inline">Summary</span>
                <span className="sm:hidden">Pipe</span>
              </div>
            </TabsTrigger>
            <TabsTrigger value="pipeCompatibility" className={PIPEFITTER_TAB_TRIGGER_CLASS}>
              <div className="flex items-center gap-1 md:gap-2">
                <span className="hidden sm:inline">Compatibility</span>
                <span className="sm:hidden">Compatibility</span>
              </div>
            </TabsTrigger> */}
            {/* <TabsTrigger value="pipeLinearGraph" className={PIPEFITTER_TAB_TRIGGER_CLASS}>
              <div className="flex items-center gap-1 md:gap-2">
                <span className="hidden sm:inline">Pipe linear graph</span>
                <span className="sm:hidden">Linear</span>
              </div>
            </TabsTrigger>
            <TabsTrigger value="pipePolarView" className={PIPEFITTER_TAB_TRIGGER_CLASS}>
              <div className="flex items-center gap-1 md:gap-2">
                <span className="hidden sm:inline">Pipe Polar view</span>
                <span className="sm:hidden">Polar</span>
              </div>
            </TabsTrigger>
            <TabsTrigger value="projectSummary" className={PIPEFITTER_TAB_TRIGGER_CLASS}>
              <div className="flex items-center gap-1 md:gap-2">
                <span className="hidden sm:inline">Project Summary</span>
                <span className="sm:hidden">Summary</span>
              </div>
            </TabsTrigger> */}
          </TabsList>
        </div>
        <TabsContent value="projectStatus" className="mt-4">
          {isReportsLoading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                <span className="text-gray-600 text-lg">Loading Pipe Fitter data...</span>
              </div>
            </div>
          ) : (
            <ProjectStatus
              projectStatusPipeData={projectStatusPipeData}
              isLoading={isReportsLoading}
              projectNumber={projectNumber || DEMO_PROJECT_NUMBER}
            />
          )}
        </TabsContent>
        <TabsContent value="pipeData" className="mt-4">
          {isReportsLoading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                <span className="text-gray-600 text-lg">Loading Pipe Fitter data...</span>
              </div>
            </div>
          ) : (
            <PipeData
              pipeStatistics={pipeStatistics}
              pipeDataGraph={pipeDataGraph}
              isLoading={isReportsLoading}
              handleSwitchChange={setShowGraphicalView}
              showGraphicalView={showGraphicalView}
              projectNumber={projectNumber || DEMO_PROJECT_NUMBER}
              canReport={canReport}
              selectedPipe={selectedPipe ?? null}
            />
          )}
        </TabsContent>
        <TabsContent value="pipeCompatibility" className="mt-4">
          {isReportsLoading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                <span className="text-gray-600 text-lg">Loading Pipe Fitter data...</span>
              </div>
            </div>
          ) : (
            <PipeCompatibility
              projectSummaryData={projectSummaryData}
              projectNumber={projectNumber}
              isLoading={isReportsLoading}
              canReport={canReport}
            />
          )}
        </TabsContent>
        <TabsContent value="pipeLinearGraph" className="mt-4">
          <PipeLinearGraph
            pipeDataGraph={pipeDataGraph}
            projectNumber={projectNumber}
            isLoading={isReportsLoading}
          />
        </TabsContent>
        <TabsContent value="pipePolarView" className="mt-4">
          <PipePolarView
            pipeDataGraph={pipeDataGraph}
            projectNumber={projectNumber}
            isLoading={isReportsLoading}
          />
        </TabsContent>
        <TabsContent value="projectSummary" className="mt-4">
          <ProjectSummary
            projectSummaryData={projectSummaryData}
            isLoading={isReportsLoading}
            projectNumber={projectNumber || DEMO_PROJECT_NUMBER}
            canReport={canReport}
          />
        </TabsContent>
      </Tabs>}
    </div>
  );
};
